import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { formatClockTime, formatTimeRange } from "./dateUtils";
import type { NurseInShift } from "./models";
import { type QuickAddConflict, QuickAddConflictKind, type ShiftWindow, type SmallOverlapTrims } from "./scheduleConflict";

/** The tone a conflict hint is shown in (a subset of `InlineHint`'s tones). */
export type QuickAddConflictHintTone = "info" | "warning";
export type QuickAddConflictHint = { tone: QuickAddConflictHintTone; message: string };

/** How a shift's location reads in the dialog's copy. */
export const shiftLocationLabel = (shift: NurseInShift, t: TFunction): string =>
	// DEFAULT: a shift with no location — missing or empty, the same falsy test the grid groups by
	// (`useCensusGrouping`) — sits in the grid's "Unassigned" group, so it is named the same here.
	shift.location || t("unassigned");

/** A window in the grid rows' own format, in the department's zone, so the copy reads like the row it refers to. */
const rangeLabel = (shift: ShiftWindow, timezone: string): string => formatTimeRange(shift.startTime.setZone(timezone), shift.endTime.setZone(timezone));

/**
 * The staff-picker tag on a row for one of a person's shifts that day: where and when, e.g. "2 South · 7:00 AM -
 * 3:00 PM". Someone with two shifts has two rows, each tagged with its own.
 */
export const scheduledChipLabel = (shift: NurseInShift, t: TFunction, timezone: string): string =>
	t("quick.add.scheduled.chip", { location: shiftLocationLabel(shift, t), time: rangeLabel(shift, timezone) });

/** What the server will cut short when an overlap is within the allowance, one sentence each. */
const trimSentences = ({ trimmed, shortenedBy }: SmallOverlapTrims, t: TFunction, timezone: string): string[] => {
	const clock = (time: DateTime) => formatClockTime(time.setZone(timezone));
	const trims = trimmed.map(({ shift, newEndTime }) => t("quick.add.double.trim.notice", { location: shiftLocationLabel(shift, t), time: clock(newEndTime) }));
	const shortened = shortenedBy
		? [t("quick.add.double.new.trim.notice", { time: clock(shortenedBy.newEndTime), location: shiftLocationLabel(shortenedBy.shift, t) })]
		: [];
	return [...trims, ...shortened];
};

/** One second-shift sentence however many shifts they already have that day, then any trims. */
const doubleShiftMessage = (conflict: Extract<QuickAddConflict, { kind: QuickAddConflictKind.Double }>, t: TFunction, timezone: string): string => {
	const [only, ...more] = conflict.shifts;
	const where = (shift: NurseInShift) => ({ time: rangeLabel(shift, timezone), location: shiftLocationLabel(shift, t) });
	const notice =
		only && more.length === 0
			? t("quick.add.double.notice", where(only))
			: t("quick.add.double.notice.multiple", { shifts: conflict.shifts.map((shift) => t("quick.add.double.notice.item", where(shift))).join(", ") });
	return [notice, ...trimSentences(conflict, t, timezone)].join(" ");
};

/**
 * The note under the staff picker saying what saving will do for this person, or `null` when there is nothing
 * to say. `location` is the dialog's destination; a move with none picked yet says where they are instead, and
 * the required Location field holds the save until one is chosen.
 */
export const buildConflictHint = ({
	conflict,
	name,
	location,
	t,
	timezone,
}: {
	conflict: QuickAddConflict;
	name: string;
	location: string | null;
	t: TFunction;
	timezone: string;
}): QuickAddConflictHint | null => {
	switch (conflict.kind) {
		case QuickAddConflictKind.None:
			return null;
		case QuickAddConflictKind.Move: {
			const { existing } = conflict;
			const time = rangeLabel(existing, timezone);
			const fromLocation = shiftLocationLabel(existing, t);
			const message =
				location === null
					? t("quick.add.same.location.notice", { name, time, location: fromLocation })
					: t("quick.add.move.notice", { name, time, fromLocation, toLocation: location });
			// The move's update settles its other small overlaps like an add would, so say so the same way.
			return { tone: "warning", message: [message, ...trimSentences(conflict, t, timezone)].join(" ") };
		}
		case QuickAddConflictKind.SameLocation: {
			const { existing } = conflict;
			return {
				tone: "warning",
				message: t("quick.add.same.location.notice", { name, time: rangeLabel(existing, timezone), location: shiftLocationLabel(existing, t) }),
			};
		}
		case QuickAddConflictKind.MultipleOverlap:
			return { tone: "warning", message: t("quick.add.multiple.overlap.notice", { name, count: conflict.overlapping.length }) };
		case QuickAddConflictKind.Double:
			return { tone: "info", message: doubleShiftMessage(conflict, t, timezone) };
	}
};
